import { useState, useMemo } from 'react'
import { Search, Target, Loader2, Trash2, Pencil } from 'lucide-react'
import { Modal } from '@/components/common/Modal'
import type { TemplateInfo } from '@/services/api/templateTraining'
import { countTokenPatterns } from '@/utils/tokenPatterns'
import { TemplateEditModal } from './TemplateEditModal'

interface TemplatesTabProps {
  templates: TemplateInfo[]
  loading: boolean
  search: string
  onSearchChange: (search: string) => void
  onDeleteTemplate: (templateId: string) => Promise<void>
  onDeleteAllTemplates?: () => Promise<void>
  onTemplateUpdated?: () => void
  onGoToFiles?: () => void
}

export function TemplatesTab({
  templates,
  loading,
  search,
  onSearchChange,
  onDeleteTemplate,
  onDeleteAllTemplates,
  onTemplateUpdated,
  onGoToFiles,
}: TemplatesTabProps) {
  const [deleteTarget, setDeleteTarget] = useState<TemplateInfo | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [editTarget, setEditTarget] = useState<TemplateInfo | null>(null)
  const [showDeleteAll, setShowDeleteAll] = useState(false)
  const [deletingAll, setDeletingAll] = useState(false)

  const filteredTemplates = useMemo(() => {
    if (!search) return templates
    const lower = search.toLowerCase()
    return templates.filter(t =>
      (t.name || '').toLowerCase().includes(lower) ||
      (t.tool_name || '').toLowerCase().includes(lower) ||
      t.id.toLowerCase().includes(lower)
    )
  }, [templates, search])

  const getTokenPatternCount = (template: TemplateInfo): number => {
    const config = template.extraction_config as { token_patterns?: Record<string, unknown> } | undefined
    return countTokenPatterns(config?.token_patterns)
  }

  const handleDelete = async () => {
    if (deleteTarget) {
      setDeleting(true)
      try {
        await onDeleteTemplate(deleteTarget.id)
      } finally {
        setDeleting(false)
        setDeleteTarget(null)
      }
    }
  }

  const handleDeleteAll = async () => {
    setDeletingAll(true)
    try {
      await onDeleteAllTemplates?.()
    } finally {
      setDeletingAll(false)
      setShowDeleteAll(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Templates laden...</span>
      </div>
    )
  }

  if (templates.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Templates</h2>
            <p className="text-xs text-gray-500 mt-0.5">Automatisch erstellt durch PDF-Extraktion</p>
          </div>
        </div>
        <div className="py-12 text-center">
          <Target className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Keine Templates vorhanden</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
            Templates werden automatisch erstellt, wenn du eine PDF extrahierst und Token zuordnest.
          </p>
          {onGoToFiles && (
            <button
              onClick={onGoToFiles}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              PDF hochladen und extrahieren
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Templates</h2>
          <p className="text-xs text-gray-500 mt-0.5">Automatisch erstellt durch PDF-Extraktion</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{templates.length} Template{templates.length !== 1 ? 's' : ''}</span>
          {onDeleteAllTemplates && templates.length > 0 && (
            <button
              onClick={() => setShowDeleteAll(true)}
              className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
            >
              Alle löschen
            </button>
          )}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Templates suchen..."
          className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {filteredTemplates.length === 0 ? (
        <div className="py-8 text-center text-gray-500 text-sm">
          Keine Templates gefunden für "{search}"
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => {
            const patternCount = getTokenPatternCount(template)
            const isPending = template.status === 'pending_review'
            const isRejected = template.status === 'rejected'
            return (
              <div
                key={template.id}
                className={`border rounded-lg p-4 hover:shadow-md transition-shadow bg-white ${isPending ? 'border-yellow-300 bg-yellow-50/30' : ''} ${isRejected ? 'border-red-300 bg-red-50/30' : ''}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm text-gray-900 truncate" title={template.name || ''}>
                        {template.name || `Template ${template.id.slice(0, 8)}`}
                      </h4>
                      {isPending && (
                        <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] rounded-full font-medium">Review</span>
                      )}
                      {isRejected && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded-full font-medium">Abgelehnt</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {template.tool_name || 'Unbekannt'} · v{template.version}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setEditTarget(template)}
                      className="text-gray-400 hover:text-blue-500 p-1 rounded transition-colors"
                      title="Bearbeiten"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(template)}
                      className="text-gray-400 hover:text-red-500 p-1 rounded transition-colors"
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-1 text-xs text-gray-500 mb-3">
                  <div>Erstellt: {new Date(template.created_at).toLocaleDateString('de-DE')}</div>
                  {patternCount > 0 && (
                    <div className="text-blue-600 font-medium">{patternCount} Token-Pattern{patternCount !== 1 ? 's' : ''}</div>
                  )}
                </div>

                {patternCount > 0 && (
                  <button
                    onClick={() => setEditTarget(template)}
                    className="text-xs text-blue-600 hover:text-blue-800 border-t pt-2 w-full text-left"
                  >
                    Token-Patterns anzeigen
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Template löschen"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Möchtest du das Template "{deleteTarget?.name || deleteTarget?.id.slice(0, 8)}" wirklich löschen?
            Dies kann nicht rückgängig gemacht werden.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setDeleteTarget(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? 'Löschen...' : 'Löschen'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteAll}
        onClose={() => setShowDeleteAll(false)}
        title="Alle Templates löschen"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Möchtest du wirklich alle <strong>{templates.length} Templates</strong> löschen?
            Dabei wird auch der Token-Speicher gelöscht. Offene Import-Shops gehen mit.
            Dies kann nicht rückgängig gemacht werden.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowDeleteAll(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDeleteAll}
              disabled={deletingAll}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
            >
              {deletingAll ? 'Löschen...' : 'Alle löschen'}
            </button>
          </div>
        </div>
      </Modal>

      <TemplateEditModal
        templateId={editTarget?.id || ''}
        templateName={editTarget?.name || editTarget?.id.slice(0, 8) || ''}
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => onTemplateUpdated?.()}
      />
    </div>
  )
}
